import type { TankDTO } from "../../../shared/protocol/index.js";
import { state } from "../../state.js";
import type { Renderer } from "../Renderer.js";
import { drawWindupStubs } from "../scope.js";
import { shade } from "../util.js";
import { drawLocalIndicator } from "./indicator.js";

/** Realistic military theme. Called inside drawTank's save/translate/alpha. */
export function drawRealisticTank(rd: Renderer, t: TankDTO, isLocal: boolean, nowMs: number): void {
  const { ctx } = rd;
  const r = rd.tankR;

  // Local-player facing indicator: a glowing sky-blue arrow ahead of your tank.
  if (isLocal && t.alive) {
    drawLocalIndicator(ctx, r, t.bodyAngle, nowMs);
  }

  // Cast shadow for every tank, sun from the top-left (matches walls/rocks).
  if (t.alive) {
    ctx.beginPath();
    ctx.ellipse(2.5, 3.2, r * 1.18, r * 1.02, 0, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(22, 18, 8, 0.22)";
    ctx.fill();
  }

  ctx.save();
  ctx.rotate(t.bodyAngle);

  // Realistic military olive/tan colors
  let hullColor = "#4a5a3c"; // Enemy: olive drab
  let darkColor = "#3a4a2c";
  if (isLocal) {
    hullColor = "#5b6b32"; // Player: woodland green
    darkColor = "#4a5a28";
  } else if (t.team !== -1) {
    const localTeam = rd.displayedSnap?.tanks.find(tk => tk.id === state.playerId)?.team;
    if (t.team === localTeam) {
      hullColor = "#6b7b42"; // Teammate: lighter green
      darkColor = "#5a6a38";
    }
  }

  // Resolve chassis style
  let style = state.realisticStyle;
  if (!isLocal) {
    const hash = t.id.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const styles = ["abrams", "leopard", "t90", "bradley"] as const;
    style = styles[hash % styles.length];
  }

  // Tracks/treads — all styles have them
  const trackW = style === "bradley" ? r * 0.3 : r * 0.38;
  const trackH = style === "leopard" ? r * 2.3 : r * 2.1;
  const trackOff = style === "bradley" ? r * 0.82 : r * 0.92;

  ctx.fillStyle = "#1a1a18";
  ctx.fillRect(-trackH/2, -trackOff - trackW/2, trackH, trackW);
  ctx.fillRect(-trackH/2, trackOff - trackW/2, trackH, trackW);
  // Track link detail — subtle horizontal ridges
  ctx.strokeStyle = "rgba(80, 75, 65, 0.5)";
  ctx.lineWidth = 0.8;
  for (let xo = -trackH/2 + 4; xo < trackH/2; xo += 5) {
    ctx.beginPath();
    ctx.moveTo(xo, -trackOff - trackW/2);
    ctx.lineTo(xo, -trackOff + trackW/2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(xo, trackOff - trackW/2);
    ctx.lineTo(xo, trackOff + trackW/2);
    ctx.stroke();
  }

  // Hull shapes per style
  if (style === "abrams") {
    // Angular front wedge hull — M1A2 style
    ctx.fillStyle = hullColor;
    ctx.beginPath();
    ctx.moveTo(-r * 0.85, -r * 0.8);
    ctx.lineTo(r * 0.5, -r * 0.8);
    ctx.lineTo(r * 0.95, -r * 0.4);
    ctx.lineTo(r * 0.95, r * 0.4);
    ctx.lineTo(r * 0.5, r * 0.8);
    ctx.lineTo(-r * 0.85, r * 0.8);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = darkColor;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    // Engine deck hatches
    ctx.fillStyle = darkColor;
    ctx.fillRect(-r * 0.8, -r * 0.35, r * 0.5, r * 0.7);
    ctx.strokeStyle = "rgba(0,0,0,0.3)";
    ctx.strokeRect(-r * 0.8, -r * 0.35, r * 0.5, r * 0.7);
  } else if (style === "leopard") {
    // Smooth wedge hull — German precision
    ctx.fillStyle = hullColor;
    ctx.beginPath();
    ctx.moveTo(-r * 0.9, -r * 0.75);
    ctx.lineTo(r * 0.7, -r * 0.65);
    ctx.lineTo(r * 1.0, 0);
    ctx.lineTo(r * 0.7, r * 0.65);
    ctx.lineTo(-r * 0.9, r * 0.75);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = darkColor;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    // Side skirts
    ctx.fillStyle = shade(hullColor, -12);
    ctx.fillRect(-r * 0.85, -r * 0.8, r * 1.5, r * 0.12);
    ctx.fillRect(-r * 0.85, r * 0.68, r * 1.5, r * 0.12);
  } else if (style === "t90") {
    // Rounded turret-forward hull — Russian school
    ctx.fillStyle = hullColor;
    ctx.beginPath();
    ctx.moveTo(-r * 0.85, -r * 0.78);
    ctx.lineTo(r * 0.6, -r * 0.78);
    ctx.quadraticCurveTo(r * 0.95, -r * 0.5, r * 0.95, 0);
    ctx.quadraticCurveTo(r * 0.95, r * 0.5, r * 0.6, r * 0.78);
    ctx.lineTo(-r * 0.85, r * 0.78);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = darkColor;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    // ERA blocks (reactive armor)
    ctx.fillStyle = shade(hullColor, -18);
    for (let bx = -r*0.3; bx < r*0.6; bx += r*0.22) {
      ctx.fillRect(bx, -r*0.82, r*0.18, r*0.12);
      ctx.fillRect(bx, r*0.7, r*0.18, r*0.12);
    }
  } else {
    // Bradley IFV — boxy and compact
    ctx.fillStyle = hullColor;
    ctx.fillRect(-r * 0.8, -r * 0.7, r * 1.6, r * 1.4);
    ctx.strokeStyle = darkColor;
    ctx.lineWidth = 1.5;
    ctx.strokeRect(-r * 0.8, -r * 0.7, r * 1.6, r * 1.4);
    // Front vision block
    ctx.fillStyle = "rgba(120, 180, 120, 0.3)";
    ctx.fillRect(r * 0.4, -r * 0.15, r * 0.25, r * 0.3);
    ctx.strokeStyle = darkColor;
    ctx.strokeRect(r * 0.4, -r * 0.15, r * 0.25, r * 0.3);
  }

  ctx.restore(); // End body rotation

  // Sun-side sheen: bright toward the fixed top-left light, shaded away
  // from it, regardless of hull orientation — sells the tank as a volume.
  if (t.alive) {
    const sheen = ctx.createLinearGradient(-r, -r, r, r);
    sheen.addColorStop(0, "rgba(255, 250, 230, 0.14)");
    sheen.addColorStop(0.5, "rgba(255, 250, 230, 0)");
    sheen.addColorStop(1, "rgba(0, 0, 0, 0.16)");
    ctx.fillStyle = sheen;
    ctx.beginPath();
    ctx.arc(0, 0, r * 1.05, 0, Math.PI * 2);
    ctx.fill();
  }

  // Turret + barrel
  ctx.save();
  ctx.rotate(t.turretAngle);

  if (style === "abrams") {
    // Long smoothbore barrel
    ctx.fillStyle = "#2a2a24";
    ctx.fillRect(r * 0.1, -2.5, r * 1.7, 5);
    ctx.fillStyle = "#1a1a18";
    ctx.fillRect(r * 1.7, -3.5, r * 0.15, 7); // muzzle brake
    // Flat angular turret
    ctx.fillStyle = shade(hullColor, 8);
    ctx.beginPath();
    ctx.moveTo(-r * 0.45, -r * 0.48);
    ctx.lineTo(r * 0.15, -r * 0.48);
    ctx.lineTo(r * 0.35, -r * 0.25);
    ctx.lineTo(r * 0.35, r * 0.25);
    ctx.lineTo(r * 0.15, r * 0.48);
    ctx.lineTo(-r * 0.45, r * 0.48);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = darkColor;
    ctx.lineWidth = 1;
    ctx.stroke();
  } else if (style === "leopard") {
    // Barrel
    ctx.fillStyle = "#2a2a24";
    ctx.fillRect(r * 0.1, -2, r * 1.8, 4);
    // Wedge turret
    ctx.fillStyle = shade(hullColor, 8);
    ctx.beginPath();
    ctx.moveTo(-r * 0.5, -r * 0.4);
    ctx.lineTo(r * 0.3, -r * 0.38);
    ctx.lineTo(r * 0.45, 0);
    ctx.lineTo(r * 0.3, r * 0.38);
    ctx.lineTo(-r * 0.5, r * 0.4);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = darkColor;
    ctx.lineWidth = 1;
    ctx.stroke();
  } else if (style === "t90") {
    // Barrel
    ctx.fillStyle = "#2a2a24";
    ctx.fillRect(r * 0.05, -2.5, r * 1.6, 5);
    // Round cast turret
    ctx.fillStyle = shade(hullColor, 8);
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.52, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = darkColor;
    ctx.lineWidth = 1;
    ctx.stroke();
  } else {
    // Bradley autocannon (small caliber)
    ctx.fillStyle = "#2a2a24";
    ctx.fillRect(r * 0.1, -1.5, r * 1.5, 3);
    // Small turret box
    ctx.fillStyle = shade(hullColor, 8);
    ctx.fillRect(-r * 0.35, -r * 0.35, r * 0.7, r * 0.7);
    ctx.strokeStyle = darkColor;
    ctx.lineWidth = 1;
    ctx.strokeRect(-r * 0.35, -r * 0.35, r * 0.7, r * 0.7);
  }

  ctx.restore();

  // Laser windup
  if (t.charging && t.alive) {
    ctx.save();
    ctx.rotate(t.turretAngle);
    ctx.globalAlpha = 0.4 + 0.3 * Math.sin(nowMs / 60);
    ctx.strokeStyle = "#ff4444";
    ctx.lineWidth = 2;
    drawWindupStubs(rd, ctx, r, t.weaponCharges);
    ctx.restore();
  }

  // Shield — subtle green force field
  if (t.shielded && t.alive) {
    ctx.save();
    const pulse = 0.9 + 0.1 * Math.sin(nowMs / 120);
    ctx.strokeStyle = "rgba(100, 180, 80, 0.4)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(0, 0, r * 1.45 * pulse, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  ctx.restore();

  // Health bar
  if (t.alive && t.maxHp > 1) {
    const bw = r * 2;
    const bx = t.x - r;
    const by = t.y - r - 6;
    const frac = Math.max(0, Math.min(1, t.hp / t.maxHp));
    ctx.fillStyle = "rgba(0, 0, 0, 0.35)";
    ctx.fillRect(bx, by, bw, 3);
    ctx.fillStyle = frac > 0.5 ? "#5a8a3a" : frac > 0.25 ? "#b8a030" : "#aa3030";
    ctx.fillRect(bx, by, bw * frac, 3);
  }

  // Name / respawn label
  ctx.font = "bold 11px 'Inter', sans-serif";
  ctx.textAlign = "center";
  if (t.alive) {
    ctx.fillStyle = isLocal ? "#c8d8a0" : "#a0a898";
    const above = t.y - r - (t.maxHp > 1 ? 13 : 8);
    const labelY = above < 10 ? t.y + r + 14 : above;
    ctx.fillText(t.name, t.x, labelY);
  } else {
    ctx.fillStyle = "#706858";
    ctx.fillText(`${Math.ceil(t.respawnIn)}`, t.x, t.y + 4);
  }
}
